/**
 * SessionExpiredModal — Modal bloqueante que aparece cuando el token JWT expira.
 * El usuario debe re-autenticarse para continuar — no se puede cerrar sin credenciales válidas.
 */
import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../services/api';

interface SessionExpiredModalProps {
  onSuccess: (token: string) => void;
}

export function SessionExpiredModal({ onSuccess }: SessionExpiredModalProps) {
  const savedEmail = localStorage.getItem('user_email') || '';
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus password if email is pre-filled
    if (savedEmail && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [savedEmail]);

  const handleSubmit = async () => {
    if (!email.trim() || !password.trim()) {
      setError('Ingresa tu email y contraseña');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await auth.login(email.trim(), password);
      localStorage.setItem('access_token', data.access_token);
      onSuccess(data.access_token);
    } catch {
      setError('Credenciales incorrectas. Intenta de nuevo.');
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const INPUT: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1px solid var(--color-border-secondary)',
    background: 'var(--color-background-secondary)',
    color: 'var(--color-text-primary)', fontSize: 14,
    boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: 400, borderRadius: 20,
        background: 'var(--color-background-primary)',
        border: '1px solid var(--color-border-secondary)',
        boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 8 }}>
            Tu sesión ha expirado
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', lineHeight: 1.5, marginBottom: 24 }}>
            Por seguridad, tu sesión se cerró automáticamente.<br />
            Ingresa tus credenciales para continuar.
          </div>
        </div>

        {/* Form */}
        <div style={{ padding: '0 28px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Email
            </label>
            <input
              style={INPUT}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="tu@email.com"
              disabled={loading}
              onKeyDown={e => e.key === 'Enter' && passwordRef.current?.focus()}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Contraseña
            </label>
            <input
              ref={passwordRef}
              style={INPUT}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          {error && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#A32D2D', fontSize: 12 }}>
              ⚠ {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              marginTop: 4, padding: '12px', borderRadius: 10, border: 'none',
              background: loading ? 'var(--color-background-secondary)' : '#3B8BD4',
              color: loading ? 'var(--color-text-tertiary)' : '#fff',
              fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {loading ? 'Verificando...' : 'Iniciar sesión'}
          </button>
        </div>
      </div>
    </div>
  );
}